'use client';

import { useAuthUser } from '@/hooks/useAuthUser';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import organizationsService from '@/services/organizations-service';

/**
 * Fixed bottom-center floating banner for org owners whose org setup
 * isn't complete. Mounted globally on the main app shell + the
 * /organizacia layout, so the user always has a visible "Continue setup"
 * affordance no matter where they are in the app.
 *
 * Non-dismissible by design: a dismiss button + 24h localStorage TTL
 * was the original v1 behavior, but it left org owners stranded with
 * no way back to /org-onboarding once they X'd it. Setup is short,
 * essential, and only shows for incomplete orgs — there's no good
 * reason to let users hide the path back. The inline OrgSetupAlert
 * inside /organizacia is the primary CTA; this banner is the
 * secondary safety net for users browsing /chat.
 */
export function OrgSetupBanner() {
  const { orgId, orgRole, isInOrg, isLoading } = useAuthUser();
  const locale = useLocale();
  const t = useTranslations('Organizations');
  const [orgStatus, setOrgStatus] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const isOwner = orgRole === 'org:owner';

  useEffect(() => {
    if (!orgId || !isInOrg || !isOwner) return;

    organizationsService
      .getOrganizationPlan(orgId)
      .then((res) => {
        const status = (res.data as { status?: string } | null)?.status ?? null;
        setOrgStatus(status);
        setLoaded(true);
      })
      .catch(() => {
        setLoaded(true);
      });
  }, [orgId, isInOrg, isOwner]);

  if (isLoading || !loaded) return null;
  if (!isInOrg || !isOwner || !orgId) return null;
  // Only suppress for terminal lifecycle states. `null` status (brand-new
  // org with no plan row yet) MUST show the banner — that's exactly when
  // setup is most incomplete.
  if (orgStatus === 'active' || orgStatus === 'suspended') return null;

  return (
    <div className='fixed bottom-4 left-1/2 -translate-x-1/2 z-[99999] max-w-lg w-[calc(100%-2rem)]'>
      <div className='flex items-center gap-2.5 rounded-lg border border-status-warning/40 bg-surface-card shadow-lg px-4 py-2.5'>
        <AlertCircle
          className='h-4 w-4 text-status-warning flex-shrink-0'
          aria-hidden='true'
        />
        <p className='flex-1 text-sm text-content-primary'>
          {t('setupBannerMessage')}
        </p>
        <a
          href={`/${locale}/org-onboarding/${orgId}`}
          className='shrink-0 flex items-center gap-1 rounded-md bg-brand-primary px-3 py-1 min-h-[44px] text-xs font-medium text-content-inverse hover:bg-brand-primary/90 transition-colors'
        >
          {t('setupBannerContinue')}
          <ArrowRight className='h-3 w-3' aria-hidden='true' />
        </a>
      </div>
    </div>
  );
}
